import { useEffect, useMemo, useState } from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";

/**
 * Phase 21 — Minimal weekly RRULE builder.
 *
 * Ships weekday checkboxes + interval spinner + optional COUNT / UNTIL.
 * A raw text field is available for advanced patterns (monthly/yearly,
 * BYSETPOS, exceptions, etc). The backend accepts any RFC 5545 rrulestr()
 * parse — this component is deliberately dumb about validation.
 */

const DAYS = [
  { code: "MO", label: "Mon" },
  { code: "TU", label: "Tue" },
  { code: "WE", label: "Wed" },
  { code: "TH", label: "Thu" },
  { code: "FR", label: "Fri" },
  { code: "SA", label: "Sat" },
  { code: "SU", label: "Sun" },
];

function parseExisting(rrule) {
  if (!rrule) return null;
  const parts = Object.fromEntries(
    rrule
      .split(";")
      .map((p) => p.split("="))
      .filter((kv) => kv.length === 2),
  );
  const days = new Set((parts.BYDAY || "").split(",").filter(Boolean));
  return {
    freq: parts.FREQ || "WEEKLY",
    interval: Number(parts.INTERVAL || 1),
    byday: days,
    count: parts.COUNT ? Number(parts.COUNT) : "",
    until: parts.UNTIL || "",
  };
}

function build({ freq, interval, byday, count, until }) {
  const bits = [`FREQ=${freq}`];
  if (interval && interval > 1) bits.push(`INTERVAL=${interval}`);
  if (freq === "WEEKLY" && byday.size) {
    bits.push(`BYDAY=${Array.from(byday).join(",")}`);
  }
  if (count) bits.push(`COUNT=${count}`);
  if (until) bits.push(`UNTIL=${until.replaceAll("-", "").replaceAll(":", "")}`);
  return bits.join(";");
}

export default function RecurrenceEditor({ value, onChange }) {
  const parsed = useMemo(() => parseExisting(value), [value]);
  const [enabled, setEnabled] = useState(!!value);
  const [freq, setFreq] = useState(parsed?.freq || "WEEKLY");
  const [interval, setInterval] = useState(parsed?.interval || 1);
  const [byday, setByday] = useState(parsed?.byday || new Set());
  const [count, setCount] = useState(parsed?.count || "");
  const [until, setUntil] = useState(parsed?.until || "");
  const [raw, setRaw] = useState(value || "");
  const [advanced, setAdvanced] = useState(false);

  useEffect(() => {
    if (!enabled) {
      onChange?.(null);
      return;
    }
    if (advanced) {
      onChange?.(raw || null);
      return;
    }
    onChange?.(build({ freq, interval, byday, count, until }));
  }, [enabled, advanced, freq, interval, byday, count, until, raw]);

  const toggleDay = (code) => {
    const next = new Set(byday);
    if (next.has(code)) next.delete(code);
    else next.add(code);
    setByday(next);
  };

  return (
    <div className="space-y-3 rounded-xl border border-border p-3" data-testid="recurrence-editor">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-sm font-medium text-primary">Repeat</div>
          <div className="text-xs text-muted-foreground">Turn on to build a recurring series.</div>
        </div>
        <Switch
          checked={enabled}
          onCheckedChange={setEnabled}
          data-testid="recurrence-enabled-toggle"
        />
      </div>

      {enabled && !advanced && (
        <>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            <div>
              <Label className="text-xs text-muted-foreground">Frequency</Label>
              <select
                value={freq}
                onChange={(e) => setFreq(e.target.value)}
                className="mt-1 h-9 w-full rounded-md border border-border bg-background px-2 text-sm"
                data-testid="recurrence-freq"
              >
                <option value="DAILY">Daily</option>
                <option value="WEEKLY">Weekly</option>
                <option value="MONTHLY">Monthly (same day)</option>
              </select>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Every</Label>
              <Input
                type="number"
                min={1}
                max={52}
                value={interval}
                onChange={(e) => setInterval(Number(e.target.value) || 1)}
                className="mt-1"
                data-testid="recurrence-interval"
              />
            </div>
          </div>
          {freq === "WEEKLY" && (
            <div className="flex flex-wrap gap-2" data-testid="recurrence-weekdays">
              {DAYS.map((d) => (
                <label
                  key={d.code}
                  className="flex cursor-pointer items-center gap-1.5 rounded-full border border-border px-2.5 py-1 text-xs"
                >
                  <Checkbox
                    checked={byday.has(d.code)}
                    onCheckedChange={() => toggleDay(d.code)}
                    data-testid={`recurrence-day-${d.code}`}
                  />
                  {d.label}
                </label>
              ))}
            </div>
          )}
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            <div>
              <Label className="text-xs text-muted-foreground">End after (count)</Label>
              <Input
                type="number"
                min={0}
                value={count}
                onChange={(e) => setCount(e.target.value)}
                placeholder="Unlimited"
                className="mt-1"
                data-testid="recurrence-count"
              />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">End on (UNTIL)</Label>
              <Input
                type="date"
                value={until}
                onChange={(e) => setUntil(e.target.value)}
                className="mt-1"
                data-testid="recurrence-until"
              />
            </div>
          </div>
        </>
      )}

      {enabled && advanced && (
        <div>
          <Label className="text-xs text-muted-foreground">Raw RRULE (RFC 5545)</Label>
          <Textarea
            value={raw}
            onChange={(e) => setRaw(e.target.value)}
            placeholder="FREQ=WEEKLY;INTERVAL=1;BYDAY=MO,WE,FR;COUNT=12"
            rows={2}
            className="mt-1 font-mono text-xs"
            data-testid="recurrence-raw"
          />
        </div>
      )}

      {enabled && (
        <button
          type="button"
          className="text-xs text-secondary underline-offset-4 hover:underline"
          onClick={() => setAdvanced((a) => !a)}
          data-testid="recurrence-advanced-toggle"
        >
          {advanced ? "Back to weekly builder" : "Advanced (raw RRULE)"}
        </button>
      )}
    </div>
  );
}
